/**
 * Art & Creations. Doc: docs/overall-plan/02-MANGO-PERSONAL/art-system
 * Full CRUD: GET/POST/PUT/DELETE /api/v1/art-system/artworks.
 */
import React, { useEffect, useState } from 'react';
import { artSystemApi, type ArtSystemArtwork } from '../services/api';
import { askConfirm } from 'shared-ui';

const ArtSystem: React.FC = () => {
  const [creations, setCreations] = useState<ArtSystemArtwork[]>([]);
  const [message, setMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [editCategory, setEditCategory] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null);

  const loadData = React.useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    setError(null);
    try {
      const res = await artSystemApi.getCreations();
      if (res?.success) {
        setCreations(Array.isArray(res?.data) ? res.data : []);
        setMessage(res?.message ?? null);
      } else setError(res?.error || 'Failed to load');
    } catch {
      setError('Failed to load');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleAdd = async () => {
    if (!newTitle.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await artSystemApi.addArtwork({ title: newTitle.trim() });
      if (res?.success) {
        setNewTitle('');
        setShowAdd(false);
        loadData(true);
      } else setError(res?.error || 'Failed');
    } catch {
      setError('Failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (c: ArtSystemArtwork) => {
    setEditingId(c.id);
    setEditTitle(c.title);
    setEditCategory(c.category || '');
  };

  const handleUpdate = async () => {
    if (!editingId || !editTitle.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await artSystemApi.updateArtwork(editingId, {
        title: editTitle.trim(),
        category: editCategory.trim() || undefined,
      });
      if (res?.success) {
        setEditingId(null);
        loadData(true);
      } else setError(res?.error || 'Failed to update');
    } catch {
      setError('Failed to update');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!(await askConfirm({ title: 'Remove this artwork?', confirmLabel: 'Remove', danger: true }))) return;
    setDeleting(id);
    setError(null);
    try {
      const res = await artSystemApi.deleteArtwork(id);
      if (res?.success) {
        if (editingId === id) setEditingId(null);
        loadData(true);
      } else setError(res?.error || 'Failed to delete');
    } catch {
      setError('Failed to delete');
    } finally {
      setDeleting(null);
    }
  };

  if (loading) {
    return (
      <div style={{ maxWidth: '560px', margin: '0 auto', padding: '24px' }}>
        <p>Loading…</p>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '560px', margin: '0 auto', padding: '24px' }}>
      <div style={{
        background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
        borderRadius: '16px',
        padding: '24px',
        marginBottom: '24px',
        color: 'white',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', flexWrap: 'wrap' }}>
          <div>
            <h1 style={{ margin: '0 0 8px 0', fontSize: '24px' }}>🎨 Art & Creations</h1>
            <p style={{ margin: 0, opacity: 0.95, fontSize: '14px' }}>
              Portfolio and rights for your creative work
            </p>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button type="button" onClick={() => setShowAdd((s) => !s)} style={{ padding: '8px 16px', background: 'rgba(255,255,255,0.9)', border: 'none', borderRadius: '8px', color: '#f5576c', cursor: 'pointer', fontSize: '14px' }}>Add Artwork</button>
            <button type="button" onClick={() => loadData(true)} disabled={refreshing} style={{ padding: '8px 16px', background: 'rgba(255,255,255,0.2)', border: '1px solid rgba(255,255,255,0.4)', borderRadius: '8px', color: 'white', cursor: refreshing ? 'not-allowed' : 'pointer', fontSize: '14px' }}>{refreshing ? 'Refreshing…' : 'Refresh'}</button>
          </div>
        </div>
      </div>
      {showAdd && (
        <div style={{ background: '#f8fafc', borderRadius: '12px', padding: '16px', marginBottom: '16px', border: '1px solid #e2e8f0' }}>
          <input type="text" placeholder="Artwork title" value={newTitle} onChange={(e) => setNewTitle(e.target.value)} style={{ width: '100%', padding: '10px 12px', marginBottom: '10px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '14px' }} />
          <div style={{ display: 'flex', gap: '8px' }}>
            <button type="button" onClick={handleAdd} disabled={submitting || !newTitle.trim()} style={{ padding: '8px 16px', background: '#16a34a', border: 'none', borderRadius: '8px', color: 'white', cursor: submitting ? 'not-allowed' : 'pointer', fontSize: '14px' }}>{submitting ? 'Adding…' : 'Add'}</button>
            <button type="button" onClick={() => { setShowAdd(false); setNewTitle(''); }} style={{ padding: '8px 16px', background: '#64748b', border: 'none', borderRadius: '8px', color: 'white', cursor: 'pointer', fontSize: '14px' }}>Cancel</button>
          </div>
        </div>
      )}
      {editingId && (
        <div style={{ background: '#f8fafc', borderRadius: '12px', padding: '16px', marginBottom: '16px', border: '2px solid #f5576c' }}>
          <h3 style={{ margin: '0 0 12px 0', fontSize: '16px' }}>Edit artwork</h3>
          <input type="text" placeholder="Artwork title" value={editTitle} onChange={(e) => setEditTitle(e.target.value)} style={{ width: '100%', padding: '10px 12px', marginBottom: '10px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '14px' }} />
          <input type="text" placeholder="Category (optional)" value={editCategory} onChange={(e) => setEditCategory(e.target.value)} style={{ width: '100%', padding: '10px 12px', marginBottom: '10px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '14px' }} />
          <div style={{ display: 'flex', gap: '8px' }}>
            <button type="button" onClick={handleUpdate} disabled={submitting || !editTitle.trim()} style={{ padding: '8px 16px', background: '#f5576c', border: 'none', borderRadius: '8px', color: 'white', cursor: submitting ? 'not-allowed' : 'pointer', fontSize: '14px' }}>{submitting ? 'Saving…' : 'Save'}</button>
            <button type="button" onClick={() => { setEditingId(null); setEditTitle(''); setEditCategory(''); }} style={{ padding: '8px 16px', background: '#64748b', border: 'none', borderRadius: '8px', color: 'white', cursor: 'pointer', fontSize: '14px' }}>Cancel</button>
          </div>
        </div>
      )}
      {error && <p style={{ color: '#b91c1c', marginBottom: '16px' }}>{error}</p>}
      {message && !error && <p style={{ marginBottom: '16px', fontSize: '14px', color: '#64748b' }}>{message}</p>}
      {creations.length === 0 && !error && !showAdd && <div style={{ background: '#f8fafc', borderRadius: '12px', padding: '32px', textAlign: 'center', color: '#64748b', fontSize: '14px' }}>No creations yet. Add an artwork above.</div>}
      {creations.length > 0 && (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {creations.map((c) => (
            <li key={c.id} style={{ padding: '12px 16px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px' }}>
              <div>
                <strong>{c.title}</strong>
                {c.category && <span style={{ marginLeft: '8px', color: '#64748b' }}>- {c.category}</span>}
                {c.isListed && <span style={{ marginLeft: '8px', fontSize: '12px', color: '#16a34a' }}>Listed</span>}
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button type="button" onClick={() => handleEdit(c)} style={{ padding: '6px 12px', background: '#f5576c', border: 'none', borderRadius: '6px', color: 'white', cursor: 'pointer', fontSize: '12px' }}>Edit</button>
                <button type="button" onClick={() => handleDelete(c.id)} disabled={deleting === c.id} style={{ padding: '6px 12px', background: '#dc2626', border: 'none', borderRadius: '6px', color: 'white', cursor: deleting === c.id ? 'not-allowed' : 'pointer', fontSize: '12px' }}>{deleting === c.id ? '…' : 'Delete'}</button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default ArtSystem;
